import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer'
import Logo from '../../../../assets/images/brand/IBSC.png';
import Stamp from '../../../../assets/images/brand/star-stamp.png';
import Border from '../../../../assets/images/certificates/IBSC.png';

const formatDateWithSuperscript = (dateString) => {
    if (!dateString) return '';

    const parts = dateString.split(' ');

    if (parts.length < 3) return dateString;

    let day = parts[0].match(/\d+/)[0];
    const suffix = parts[0].replace(/\d+/, '');
    const month = parts[1];
    const year = parts[2];

    day = day.padStart(2, '0');

    return { day, suffix, month, year };
};

const splitTextAndDate = (input = "") => {
    const text = normalizeInput(input);

    const dateRegex = /(\d{1,2})(st|nd|rd|th)\s+([A-Za-z]+)\s+(\d{4})/i;
    const match = text.match(dateRegex);

    if (!match) {
        return {
            prefixText: text,
            day: null,
            suffix: null,
            month: null,
            year: null,
        };
    }

    const [fullDate, day, suffix, month, year] = match;

    const prefixText = text.replace(fullDate, "").trim();

    return {
        prefixText,
        day,
        suffix,
        month,
        year,
    };
};

const normalizeInput = (text = "") => {
    return text
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, "") // control chars
        .replace(/ᵗʰ/g, "th")
        .replace(/ˢᵗ/g, "st")
        .replace(/ⁿᵈ/g, "nd")
        .replace(/ʳᵈ/g, "rd")
        .trim();
};

const IBSCOfficialCertificate = ({ data }) => {
    const formattedDate = formatDateWithSuperscript(data[0]?.completion_date);
    const formattedCertificateDate = formatDateWithSuperscript(data[0]?.certificate_date);

    const { prefixText, day, suffix, month, year } =
        splitTextAndDate(data[0]?.issued_date);

    Font.register({
        family: 'Open Sans',
        src: require('../../../../assets/fonts/OpenSans-Regular.ttf')
    });
    Font.register({
        family: 'Open Sans Bold',
        src: require('../../../../assets/fonts/OpenSans-SemiBold.ttf')
    });
    Font.register({
        family: 'Open Sans Extra Bold',
        src: require('../../../../assets/fonts/OpenSans-Bold.ttf')
    });

    // Styles
    const styles = StyleSheet.create({
        page: {
            fontFamily: 'Open Sans',
            position: 'relative',
            padding: 0,
        },
        borderImage: {
            position: 'absolute',
            top: -4.5,
            left: -3,
            width: '101%',
            height: '101%',
            objectFit: 'cover',
        },
        contentWrapper: {
            paddingTop: 55,
            paddingBottom: 25,
            paddingHorizontal: 20,
            height: '100%',
        },
        text: {
            fontFamily: 'Open Sans',
            fontSize: 16,
            textAlign: 'justify',
            fontWeight: 100,
            marginBottom: 20,
        },
        stuName: {
            fontFamily: 'Open Sans Bold',
            fontSize: 20,
            textAlign: 'justify',
            marginBottom: 20,
        },
        degreeText: {
            fontFamily: 'Open Sans Bold',
            fontSize: 20,
            textAlign: 'justify',
            marginTop: 20,
            marginBottom: 20,
        },
        textSentence: {
            fontFamily: 'Open Sans',
            fontSize: 16,
            textAlign: 'justify',
            fontWeight: 100,
            marginBottom: 5,
        },
        detailsText: {
            fontFamily: 'Open Sans',
            fontSize: 8,
            textAlign: 'justify',
            fontWeight: 600,
            marginBottom: 2,
        },
        regText: {
            fontFamily: 'Open Sans Extra Bold',
            fontSize: 9,
            textAlign: 'justify',
            marginTop: 8,
        },
        signatureText: {
            fontFamily: 'Open Sans',
            fontSize: 12,
            textAlign: 'justify',
            fontWeight: 200,
            marginTop: 30,
            marginBottom: 20,
        },
        signatureTextBottom: {
            fontFamily: 'Open Sans',
            fontSize: 12,
            textAlign: 'justify',
            fontWeight: 200,
            marginTop: 50,
        },
        scanText: {
            fontSize: 5,
            textAlign: 'justify',
            fontWeight: 100,
            marginBottom: 20,
        },
        superscript: {
            fontSize: 10,
            verticalAlign: 'super',
        },
        image: {
            width: 125
        },
        logoSec: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 25,
        },
        approveSec: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 20
        },
        leftSec: {
            flex: 1,
            alignItems: "center",
        },
        stampSec: {
            flex: 1,
            alignItems: "center",
        },
        stampImage: {
            width: 195,
        },
        rightSec: {
            flex: 1,
            alignItems: "center",
        },
        qrImage: {
            marginTop: 15,
            width: 70,
            height: 70,
        },
        bodySec: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            marginVertical: 10,
        },
    });

    // if (!qrCodeDataUri) return <Text>Loading certificate...</Text>


    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.contentWrapper}>
                    <View style={styles.logoSec}>
                        <Image style={styles.image} src={data[0]?.certificate_template?.header_logo} />
                    </View>

                    {/* Certificate Text */}
                    <View style={styles.bodySec}>
                        <Text style={styles.text} hyphenationCallback={(word) => [word]}>{data[0]?.certificate_template_version?.certificate_phrase_1}</Text>
                        <Text style={styles.stuName} hyphenationCallback={(word) => [word]}>{data[0]?.student_name || 'xxxx xxxxxx xxxx'}</Text>
                        <Text style={styles.textSentence} hyphenationCallback={(word) => [word]}>
                            {data[0]?.certificate_template_version?.certificate_phrase_2}
                        </Text>
                        <Text style={styles.textSentence} hyphenationCallback={(word) => [word]}>
                            {data[0]?.certificate_template_version?.certificate_phrase_3}
                        </Text>
                        <Text style={styles.degreeText} hyphenationCallback={(word) => [word]}>{data[0]?.course_batch?.course.name}</Text>
                        <Text style={styles.textSentence} hyphenationCallback={(word) => [word]}>{data[0]?.certificate_template_version?.conducted_by_phrase}</Text>
                        <Text style={styles.textSentence}>IBS College of TVET</Text>
                        {/* <Text style={styles.textSentence}>on {formattedCertificateDate.day}<Text style={styles.superscript}>{formattedCertificateDate.suffix}</Text> {formattedCertificateDate.month} {formattedCertificateDate.year}
                        </Text> */}
                        {/* <Text style={styles.textSentence}>on 23<Text style={styles.superscript}>rd</Text> April 2024</Text> */}
                        <Text style={styles.textSentence} hyphenationCallback={(word) => [word]}> {data[0]?.certificate_template_version?.completion_phrase}
                            {prefixText} {day}
                            {suffix && (
                                <Text style={styles.superscript}>
                                    {suffix}
                                </Text>
                            )}{" "}
                            {month} {year}
                        </Text>
                    </View>

                    {/* Approval Section with Three Columns */}
                    <View style={styles.approveSec}>
                        {/* Left Section */}
                        <View style={styles.leftSec}>
                            <Text style={styles.signatureText} hyphenationCallback={(word) => [word]}>{data[0]?.certificate_template_version?.signature_designation_1}</Text>
                            <Text style={styles.signatureTextBottom} hyphenationCallback={(word) => [word]}>{data[0]?.certificate_template_version?.signature_designation_2}</Text>
                        </View>

                        {/* Middle Section (Stamp) */}
                        <View style={styles.stampSec}>
                            <Image style={styles.stampImage} src={data[0]?.certificate_template?.footor_logo} />
                        </View>
                        <View style={styles.rightSec}>
                            <Image style={styles.qrImage} src={data[1]} />
                            <Text style={styles.scanText}>Scan to verify certificate.</Text>
                            <Text style={styles.detailsText}>Document Ref: {data[0]?.document_ref}</Text>
                            <Text style={styles.detailsText}>Completion Date: {formattedDate.day} {formattedDate.month} {formattedDate.year}</Text>
                            <Text style={styles.regText}>NTC REG#: 010</Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
}

export default IBSCOfficialCertificate;